// hooks/useFormHandlers.js
import { useCallback } from "react";
import { getRestrictiveValue } from "../utils/utils";
import {
  DIVIDEND_RESTRICTIVE_VALIDATION_RULES,
  RESTRICTIVE_VALIDATION_RULES,
} from "../pages/IEPFReport/validationRules";

const useFormHandlers = (setFormData, setDividendEntries) => {
  const handleChange = useCallback(
    (e) => {
      const { name, value } = e.target;
      const fieldRules = RESTRICTIVE_VALIDATION_RULES[name];
      let newValue = value;

      if (fieldRules) {
        newValue = getRestrictiveValue(newValue, fieldRules);

        if (fieldRules.min !== undefined && newValue !== "") {
          if (
            !Number.isNaN(newValue) &&
            parseFloat(newValue) < fieldRules.min
          ) {
            newValue = String(fieldRules.min);
          }
        }
      }

      setFormData((prevData) => ({
        ...prevData,
        [name]: newValue,
      }));
    },
    [setFormData]
  );

  const cleanFinancialYearInput = (value) => {
    let newValue = value.replace(/[^\d-]/g, ""); // Remove all except digits and dash

    if (newValue.length === 4 && !newValue.includes("-")) {
      newValue += "-";
    }

    if (newValue.length > 9) {
      newValue = newValue.slice(0, 9);
    }

    if (newValue.includes("-")) {
      const parts = newValue.split("-");

      if (parts.length > 2) {
        newValue = `${parts[0]}-${parts[1].slice(0, 4)}`;
      }

      if (parts[1] && parts[1].length > 4) {
        newValue = `${parts[0]}-${parts[1].slice(0, 4)}`;
      }

      if (parts[0].length === 4 && parts[1]?.length === 4) {
        const startYear = parseInt(parts[0], 10);
        const endYear = parseInt(parts[1], 10);
        if (endYear < startYear) {
          return null; // Invalid range
        }
      }
    }

    return newValue;
  };

  const handleDividendChange = useCallback(
    (index, field, value) => {
      let newValue;

      if (field === "financialYear") {
        newValue = cleanFinancialYearInput(value);
        if (newValue === null) return; // Reject invalid range
      } else {
        newValue = value;

        const fieldRules = DIVIDEND_RESTRICTIVE_VALIDATION_RULES[field];
        if (fieldRules) {
          newValue = getRestrictiveValue(newValue, fieldRules);

          if (fieldRules.min !== undefined && newValue !== "") {
            if (
              !Number.isNaN(newValue) &&
              parseFloat(newValue) < fieldRules.min
            ) {
              newValue = String(fieldRules.min);
            }
          }
        }
      }

      setDividendEntries((prev) => {
        const newEntries = [...prev];

        if (field === "financialYear") {
          const [, end] = newValue.split("-");
          const currentDate = new Date();
          const currentYear = currentDate.getFullYear();

          if (end?.length === 4) {
            const endYear = parseInt(end, 10);
            if (endYear >= currentYear) {
              // If the end year is this year or in the future, use today's date
              const [dateOnly] = currentDate.toISOString().split("T"); // ✅ Destructured
              newEntries[index].maxPaymentDate = dateOnly;
            } else {
              // Otherwise, use December 31 of the end year
              newEntries[index].maxPaymentDate = `${end}-12-31`;
            }
          } else {
            newEntries[index].maxPaymentDate = "";
          }
        }

        newEntries[index][field] = newValue;
        return newEntries;
      });
    },
    [setDividendEntries]
  );
  const handleAddDividend = useCallback(() => {
    setDividendEntries((prev) => [
      ...prev,
      {
        financialYear: "",
        paymentDate: "",
        warrantNo: "",
        amount: "",
      },
    ]);
  }, [setDividendEntries]);

  const handleRemoveDividend = useCallback(
    (index) => {
      setDividendEntries((prev) => prev.filter((_, i) => i !== index));
    },
    [setDividendEntries]
  );

  return {
    handleChange,
    handleDividendChange,
    handleAddDividend,
    handleRemoveDividend,
  };
};

export default useFormHandlers;
